// src/features/urlaubsplaner/services/urlaubService.ts

import { supabase } from '../../../shared/services/supabase';
import { StaffUrlaubMitName, UrlaubFormData } from '../types';

class UrlaubService {

  /**
   * Lädt alle Abwesenheiten für einen Monat/Jahr mit Staff-Namen
   */
  async getAbwesenheitenFuerMonat(monat: number, jahr: number): Promise<StaffUrlaubMitName[]> {
    try {
      // Ersten und letzten Tag des Monats berechnen
      const ersterTag = `${jahr}-${String(monat).padStart(2, '0')}-01`;
      const letzterTag = new Date(jahr, monat, 0).getDate();
      const bisTag = `${jahr}-${String(monat).padStart(2, '0')}-${String(letzterTag).padStart(2, '0')}`;

      const { data, error } = await supabase
        .from('staff_urlaub')
        .select(`
          *,
          staff:xtax_staff!staff_urlaub_staff_id_fkey (
            full_name,
            initials
          )
        `)
        .lte('von', bisTag)
        .gte('bis', ersterTag)
        .order('von', { ascending: true });

      if (error) {
        console.error('Fehler beim Laden der Abwesenheiten:', error);
        return [];
      }

      if (!data) return [];

      return data.map((eintrag: any) => ({
        id: eintrag.id,
        staff_id: eintrag.staff_id,
        von: eintrag.von,
        bis: eintrag.bis,
        art: eintrag.art,
        ist_halber_tag: eintrag.ist_halber_tag || false,
        notiz: eintrag.notiz,
        created_at: eintrag.created_at,
        updated_at: eintrag.updated_at,
        staff_name: eintrag.staff?.full_name || 'Unbekannt',
        staff_initials: eintrag.staff?.initials || '??'
      }));

    } catch (error) {
      console.error('Fehler beim Laden der Abwesenheiten:', error);
      return [];
    }
  }

  /**
   * Lädt alle Abwesenheiten für ein ganzes Jahr mit Staff-Namen
   */
  async getAbwesenheitenFuerJahr(jahr: number): Promise<StaffUrlaubMitName[]> {
    try {
      const jahresAnfang = `${jahr}-01-01`;
      const jahresEnde = `${jahr}-12-31`;

      const { data, error } = await supabase
        .from('staff_urlaub')
        .select(`
          *,
          staff:xtax_staff!staff_urlaub_staff_id_fkey (
            full_name,
            initials
          )
        `)
        .lte('von', jahresEnde)
        .gte('bis', jahresAnfang)
        .order('von', { ascending: true });

      if (error) {
        console.error('Fehler beim Laden der Jahres-Abwesenheiten:', error);
        return [];
      }

      if (!data) return [];

      return data.map((eintrag: any) => ({
        id: eintrag.id,
        staff_id: eintrag.staff_id,
        von: eintrag.von,
        bis: eintrag.bis,
        art: eintrag.art,
        ist_halber_tag: eintrag.ist_halber_tag || false,
        notiz: eintrag.notiz,
        created_at: eintrag.created_at,
        updated_at: eintrag.updated_at,
        staff_name: eintrag.staff?.full_name || 'Unbekannt',
        staff_initials: eintrag.staff?.initials || '??'
      }));

    } catch (error) {
      console.error('Fehler beim Laden der Jahres-Abwesenheiten:', error);
      return [];
    }
  }

  /**
   * Erstellt eine neue Abwesenheit
   */
  async erstelleAbwesenheit(formData: UrlaubFormData): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from('staff_urlaub')
        .insert([{
          staff_id: formData.staff_id,
          von: formData.von,
          bis: formData.bis,
          art: formData.art,
          ist_halber_tag: formData.ist_halber_tag || false,
          notiz: formData.notiz || null
        }]);

      if (error) {
        console.error('Fehler beim Erstellen der Abwesenheit:', error);
        return { success: false, error: error.message };
      }

      return { success: true };

    } catch (error) {
      console.error('Fehler beim Erstellen der Abwesenheit:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unbekannter Fehler' 
      };
    }
  }

  /**
   * Aktualisiert eine bestehende Abwesenheit
   */
  async aktualisiereAbwesenheit(id: string, formData: UrlaubFormData): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from('staff_urlaub')
        .update({
          staff_id: formData.staff_id,
          von: formData.von,
          bis: formData.bis,
          art: formData.art,
          ist_halber_tag: formData.ist_halber_tag || false,
          notiz: formData.notiz || null,
          updated_at: new Date().toISOString()
        })
        .eq('id', id);

      if (error) {
        console.error('Fehler beim Aktualisieren der Abwesenheit:', error);
        return { success: false, error: error.message };
      }

      return { success: true };

    } catch (error) {
      console.error('Fehler beim Aktualisieren der Abwesenheit:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unbekannter Fehler' 
      };
    }
  }

  /**
   * Löscht eine Abwesenheit
   */
  async loescheAbwesenheit(id: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from('staff_urlaub')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Fehler beim Löschen der Abwesenheit:', error);
        return { success: false, error: error.message };
      }

      return { success: true };

    } catch (error) {
      console.error('Fehler beim Löschen der Abwesenheit:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unbekannter Fehler' 
      };
    }
  }
}

// Singleton Instance
export const urlaubService = new UrlaubService();
